<template>
    <div class="gpt3cta-preview">
        <CampaignDataProvider
            v-if="startSearch"
            :key="searchOptions.searchId + '-' + refreshCounter"
            :options="requestOptions"
            @data="onData"
            @loading="onLoading"
            @error="onError"
        />

        <hox-alert v-if="errorMessage" type="danger" size="small" margin-top="none" margin-bottom="none">
            <template #title>
                <span :title="errorMessage">
                    Sorry, the generated content was rejected by our automated content moderation system.
                </span>
            </template>
            <template #content />
            <template #actionItems>
                <Button type="primary" @click="forceRefetch">Try again</Button>
            </template>
        </hox-alert>
        <div v-else>
            <div v-if="isLoading" class="gpt3cta-preview__result-loading">
                <hox-loading-spinner :size="70" icon="wish-icon" />
            </div>
            <div v-else class="gpt3cta-preview__result">
                <div v-for="cta in ctas" :key="cta" class="gpt3cta-preview__overlay">
                    <div class="gpt3cta-preview__cta gpt3cta-preview__result-element">
                        <h4>
                            {{ cta }}
                        </h4>
                        <Button type="primary" @click="onSelect(cta)">Select</Button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script lang="ts">
import { defineComponent, type PropType } from "vue";

import CampaignDataProvider from "@/components/Campaign/CampaignDataProvider.vue";
import type { GptSearch } from "@/types/types";

const sanitizeGPTParams = ({ text, renderModel, ...rest }: GptSearch) => rest;

const getGPT3CTA = (searchOptions: ReturnType<typeof sanitizeGPTParams>) => {
    const template = `Consider this sentence:
        ${searchOptions.prompt || "$input1$"}
        Detect the language of the text and return the results ONLY in that language.
        Pick your most unique and clever idea that would be 'award winning' from a marketing perspective.

        A CALL TO ACTION is a short, three to five word sentence that excites the audience to take action.
        Generate four distinct CALL TO ACTIONS for this idea.

        Return ONLY the javascript object as a response with the callToAction property and nothing else in the EXACT following JavaScript object format
        {"callToAction": "", "callToAction": "", "callToAction": "", "callToAction": ""}`;

    return template;
};

function fieldsToRequest(fields: GptSearch, refreshCounter = 0) {
    const sanitizedFields = sanitizeGPTParams(fields);

    return {
        type: "OPENAI_AZURE_GPT_CHAT_COMPLETION",
        input: {
            model: "gpt-4",
            temperature: 0.7,
            max_tokens: 256,
            top_p: 1,
            frequency_penalty: 0.2,
            presence_penalty: 0.2,
            messages: [
                {
                    role: "system",
                    content: getGPT3CTA(sanitizedFields)
                }
            ],
            uuid: `${crypto.randomUUID()}-${refreshCounter}`
        }
    };
}

export default defineComponent({
    name: "Gpt3CtaPreview",
    components: { CampaignDataProvider },
    props: {
        searchOptions: {
            required: true,
            type: Object as PropType<GptSearch>
        },

        startSearch: {
            type: Boolean,
            default: true
        }
    },

    emits: ["selected", "complete"],

    data() {
        return {
            ctas: undefined as string[] | undefined,
            data: null as null | { output: { success: { choices: { message: { content: string } }[] } } },
            errorMessage: "",
            isLoading: true,
            refreshCounter: 0
        };
    },
    computed: {
        requestOptions() {
            return fieldsToRequest(this.searchOptions);
        }
    },

    methods: {
        onData(search: { output: { success: { choices: { message: { content: string } }[] } } }) {
            this.data = search;
            this.parseResponseText();
            this.$emit("complete");
        },

        onError({ message }: { message: string | { errorMessage: string } }) {
            this.errorMessage = typeof message === "string" ? message : message.errorMessage;
            this.isLoading = false;
            this.$emit("complete");
        },

        onLoading(loading: boolean) {
            this.isLoading = loading;
        },

        parseResponseText() {
            const response = this.data?.output.success.choices[0].message.content.trim() ?? "";
            const regex = /"callToAction":\s*"([^"]+)"/g;
            let match;
            const callToActions = [];

            do {
                match = regex.exec(response);
                if (match) {
                    callToActions.push(match[1]);
                }
            } while (match);

            this.ctas = callToActions;
        },

        forceRefetch() {
            this.errorMessage = "";
            this.refreshCounter += 1;
        },

        onSelect(cta: string) {
            this.$emit("selected", cta);
        }
    }
});
</script>

<style lang="scss">
@use "@style/variables" as *;

.gpt3-preview {
    &__image-prompt {
        background: rgba($color: #000000, $alpha: 0.5);
        color: $white;
        opacity: 0;

        &:hover {
            opacity: 1;
        }
    }

    &__image {
        position: absolute;
        top: 0;
        left: 0;
        z-index: 1;
        width: 100%;
        height: 100%;
    }
}
.gpt3cta-preview__cta {
    display: flex;
    align-items: baseline;
    h4 {
        font-weight: 400;
    }
    button {
        margin: 0 0 10px auto;
    }
}
</style>
